import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight, Users, UserPlus, TrendingUp, LogOut, Target, Building2, GitBranch, FileSignature, CalendarRange, PenTool, Rocket, BarChart3, Scale, CalendarCheck, ReceiptText, Banknote, LineChart, Boxes, ShoppingCart, Factory, Inbox, Stethoscope, Bot, AlarmClock, Clock } from "lucide-react";
import { Reveal, EASE } from "./Reveal";

const DEMO = { demoUrl: "https://fin-reco.demo.intugle.ai/", demoImg: "/assets/fin-reco-demo.png" };

const HR = {
  title: "Reimagine the Employee Lifecycle with Agentic AI",
  subtitle:
    "Transform how HR teams hire, onboard, support, develop and manage employees with intelligent AI agents that understand workforce context, make decisions, coordinate work and take action across enterprise systems.",
  useCaseName: "Hire-to-Retire",
  useCaseDesc:
    "Transform the complete employee lifecycle with AI agents working across every stage of the employee journey.",
  journey: ["Attract & Hire", "Onboard", "Serve", "Develop & Retain", "Offboard"],
  agentsHeading: "AI Agents at Work Across the Employee Lifecycle",
  agents: [
    { icon: Users, title: "Talent Acquisition", desc: "Evaluate, reason and coordinate hiring decisions — assessing candidate-role fit, synthesizing interview feedback and moving candidates through the hiring journey." },
    { icon: UserPlus, title: "Employee Onboarding", desc: "Coordinate onboarding across teams and resolve blockers — orchestrating documentation, access, assets, payroll and training across HR, IT and Finance." },
    { icon: TrendingUp, title: "Performance & Talent", desc: "Turn workforce signals into talent decisions — synthesizing goals, performance, skills and context to recommend development, mobility and retention actions." },
    { icon: LogOut, title: "Employee Offboarding", desc: "Securely orchestrate a compliant exit — coordinating knowledge transfer, access revocation, asset recovery and payroll while validating compliance." },
  ],
};

const SALES = {
  title: "Turn Every Sales Signal into Revenue Action",
  subtitle:
    "Transform how sales teams identify opportunities, engage accounts, progress deals and close business with intelligent AI agents that understand customer context, reason over commercial signals, coordinate actions and proactively drive revenue outcomes.",
  useCaseName: "Lead-to-Revenue",
  useCaseDesc: "Transform the complete sales lifecycle with AI agents working across every stage of the customer journey.",
  journey: ["Prospect", "Qualify", "Engage", "Convert", "Expand"],
  agentsHeading: "AI Agents at Work Across the Revenue Lifecycle",
  agents: [
    { icon: Target, title: "Lead & Opportunity Qualification", desc: "Evaluate, prioritize and route the highest-value opportunities — scoring fit and buying signals to focus sellers on the deals most likely to convert." },
    { icon: Building2, title: "Account Intelligence", desc: "Turn fragmented signals into actionable account intelligence — synthesizing CRM data, conversations and external signals to surface opportunities, risks and next actions." },
    { icon: GitBranch, title: "Pipeline & Deal Management", desc: "Reason over deal progression and remove barriers to closure — flagging stalled deals and risks and coordinating actions across sales, presales, legal and finance." },
    { icon: FileSignature, title: "Quote-to-Close", desc: "Orchestrate pricing, proposals, approvals and contracting — reasoning over pricing and terms to drive deals from proposal to signed contract." },
  ],
};

const MARKETING = {
  title: "Orchestrate Marketing from Strategy to Conversion",
  subtitle:
    "Transform how marketing teams plan, create, activate and optimize campaigns with intelligent AI agents that understand brand, audience and performance context, coordinate work across teams and channels, and continuously drive better marketing outcomes.",
  useCaseName: "Campaign-to-Lead",
  useCaseDesc: "Transform the complete campaign lifecycle with AI agents working across every stage of planning, content, activation, engagement and conversion.",
  journey: ["Plan", "Create", "Activate", "Engage", "Convert"],
  agentsHeading: "AI Agents at Work Across the Marketing Lifecycle",
  agents: [
    { icon: CalendarRange, title: "Campaign Planning", desc: "Turn objectives and briefs into intelligent marketing plans — recommending channels, media mix, targeting and timelines from goals, audience and past performance." },
    { icon: PenTool, title: "Content Operations", desc: "Coordinate content creation, review and approval while protecting brand consistency — generating and adapting content and clearing bottlenecks across channels." },
    { icon: Rocket, title: "Campaign Execution", desc: "Orchestrate campaign activation across teams, agencies and platforms — coordinating assets, approvals, budgets and audiences so campaigns launch on time." },
    { icon: BarChart3, title: "Marketing Performance Optimization", desc: "Continuously reason over performance and recommend next-best actions — analyzing spend, engagement and attribution to shift budgets and optimize in-flight." },
  ],
};

const FINANCE = {
  ...DEMO,
  title: "Move Finance from Manual Control to Intelligent Execution",
  subtitle:
    "Transform how finance teams reconcile, close, control and report with intelligent AI agents that understand financial context, investigate exceptions, coordinate approvals and take governed actions across enterprise systems.",
  useCaseName: "Record-to-Report",
  useCaseDesc: "Transform the complete finance lifecycle with AI agents working across every stage of financial operations and close.",
  journey: ["Record", "Reconcile", "Resolve", "Close", "Report"],
  agentsHeading: "AI Agents at Work Across the Finance Lifecycle",
  agents: [
    { icon: Scale, title: "Finance Reconciliation", desc: "Investigate exceptions, determine root causes and drive resolution — matching transactions, flagging discrepancies and coordinating closure with business owners." },
    { icon: CalendarCheck, title: "Month-End Close", desc: "Coordinate close activities and remove blockers — monitoring reconciliations, journals and approvals to accelerate close-readiness across finance teams." },
    { icon: ReceiptText, title: "Intelligent Invoice Processing", desc: "Understand, validate and process invoices end to end — matching against POs and receipts, flagging exceptions and updating ERP and procurement systems." },
    { icon: Banknote, title: "Order-to-Cash", desc: "Orchestrate billing, receivables and collections through to cash — flagging collection risks and coordinating disputes, follow-ups and cash application." },
  ],
};

const SUPPLY = {
  ...DEMO,
  demoUrl: "https://inv-opti.demo.intugle.ai/",
  demoImg: "https://customer-assets-rejwkqb3.emergentagent.net/job_intugle-ai-platform/artifacts/gxwu38lx_image.webp",
  demoDomain: "inv-opti.demo.intugle.ai",
  demoLabel: "Inventory Optimisation",
  demoAlt: "Inventory Intelligence Copilot demo application",
  title: "Build a More Adaptive and Autonomous Supply Chain",
  subtitle:
    "Transform how supply chain teams plan, procure, manage inventory and respond to disruptions with intelligent AI agents that understand operational context, reason over changing conditions and coordinate actions across the supply network.",
  useCaseName: "Plan-to-Fulfil",
  useCaseDesc: "Transform the complete supply chain lifecycle with AI agents working across planning, sourcing, inventory and fulfilment.",
  journey: ["Plan", "Source", "Stock", "Fulfil", "Optimize"],
  agentsHeading: "AI Agents at Work Across the Supply Chain",
  agents: [
    { icon: LineChart, title: "Demand & Supply Planning", desc: "Reason over demand and supply signals to improve planning — analyzing forecasts, inventory and capacity to spot gaps and recommend plan adjustments." },
    { icon: Boxes, title: "Inventory Optimization", desc: "Set the right inventory position while balancing service and working capital — reasoning over demand, lead times and risk to recommend replenishment and rebalancing." },
    { icon: ShoppingCart, title: "Procurement Operations", desc: "Coordinate sourcing and purchasing with intelligence and speed — interpreting requirements, supplier context and policies to recommend actions and execute workflows." },
    { icon: Factory, title: "Supplier Management", desc: "Assess supplier performance, risk and interventions — synthesizing delivery, quality and risk signals to flag issues and coordinate resolution." },
  ],
};

const SERVICE = {
  title: "Move from Case Management to Intelligent Resolution",
  subtitle:
    "Transform how service teams understand, resolve and prevent customer issues with intelligent AI agents that reason over customer context, determine the right resolution and take action across enterprise systems.",
  useCaseName: "Case-to-Resolution",
  useCaseDesc: "Transform the complete service lifecycle with AI agents working across every stage of customer issue resolution.",
  journey: ["Understand", "Diagnose", "Decide", "Resolve", "Learn"],
  agentsHeading: "AI Agents at Work Across the Service Lifecycle",
  agents: [
    { icon: Inbox, title: "Intelligent Case Triage", desc: "Understand customer intent, urgency and context as a case enters — classifying, prioritizing and routing work to the right agent, team or workflow." },
    { icon: Stethoscope, title: "Issue Diagnosis", desc: "Reason across customer and product context to find the root cause — synthesizing history, knowledge and prior cases to recommend resolution paths." },
    { icon: Bot, title: "Autonomous Service Execution", desc: "Move beyond recommendations to resolve requests — executing approved actions across CRM, billing and service systems, escalating to humans only when needed." },
    { icon: AlarmClock, title: "SLA & Escalation Management", desc: "Proactively identify service risks before they impact customers — monitoring progress and commitments to predict SLA breaches and coordinate escalations." },
  ],
};

const FUNCTIONS = [
  { id: "sales", name: "Sales", data: SALES },
  { id: "marketing", name: "Marketing", data: MARKETING },
  { id: "finance", name: "Finance & Accounting", data: FINANCE },
  { id: "supplychain", name: "Supply Chain", data: SUPPLY },
  { id: "cs", name: "Customer Service", data: SERVICE },
  { id: "hr", name: "HR & People Ops", data: HR },
];

const Thumb = ({ url, img, domain = "fin-reco.demo.intugle.ai", label = "Finance Close", alt = "Finance reconciliation demo application" }) => (
  <a
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    data-testid="function-demo-thumb"
    className="group flex flex-col h-full rounded-xl overflow-hidden border border-[#3E2818]/12 shadow-[0_18px_48px_-30px_rgba(62,40,24,0.5)] hover:-translate-y-0.5 transition-all duration-300"
  >
    {/* Mac-style browser chrome */}
    <div className="shrink-0 flex items-center gap-2 px-3.5 py-2.5 bg-[#F1EDE6] border-b border-[#3E2818]/8">
      <span className="w-2.5 h-2.5 rounded-full bg-[#E88B6B]" />
      <span className="w-2.5 h-2.5 rounded-full bg-[#E8C15E]" />
      <span className="w-2.5 h-2.5 rounded-full bg-[#9CB88A]" />
      <span className="ml-2 flex-1 truncate rounded-md bg-white/70 px-2.5 py-1 font-mono2 text-[10px] text-[#3E2818]/55">{domain}</span>
    </div>
    <div className="relative flex-1 aspect-[16/11] lg:aspect-auto overflow-hidden">
      <img src={img} alt={alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]" />

      {/* Light cream gradient (default) — fades out on hover */}
      <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[#F7F5F0] via-[#F7F5F0]/70 to-transparent transition-opacity duration-300 group-hover:opacity-0" />
      {/* Dark overlay — fades in uniformly on hover */}
      <div className="absolute inset-0 bg-[#211307]/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      {/* Caption (recolors on hover to stay readable) */}
      <div className="absolute inset-x-0 bottom-0 p-4 flex items-end justify-between">
        <div>
          <p className="font-mono2 text-[10px] uppercase tracking-[0.22em] text-[#8A5730] group-hover:text-[#FFB84D] transition-colors duration-300">{label}</p>
          <p className="text-[15px] font-semibold text-[#3E2818] group-hover:text-white mt-0.5 transition-colors duration-300">Explore the live demo</p>
        </div>
        <span className="w-9 h-9 rounded-full bg-[#211307] text-white group-hover:bg-white group-hover:text-[#211307] flex items-center justify-center shrink-0 transition-colors duration-300"><ArrowUpRight size={18} strokeWidth={2.4} /></span>
      </div>
    </div>
  </a>
);

const ComingSoon = () => (
  <div
    data-testid="function-demo-coming-soon"
    className="flex flex-col items-center justify-center h-full min-h-[220px] rounded-xl border border-dashed border-[#3E2818]/12 bg-[#FBF9F5]/60 text-center px-6 py-12"
  >
    <span className="w-9 h-9 rounded-full bg-[#3E2818]/[0.04] text-[#3E2818]/30 flex items-center justify-center mb-3"><Clock size={16} strokeWidth={1.6} /></span>
    <p className="text-[13px] font-medium text-[#3E2818]/40">Interactive demo coming soon</p>
  </div>
);

const Detail = ({ f }) => {
  if (!f.data) {
    return (
      <div className="rounded-2xl border border-dashed border-[#3E2818]/20 bg-white/60 py-20 text-center">
        <p className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#8A5730]">{f.name}</p>
        <p className="text-sm text-[#3E2818]/50 mt-2">Solutions for {f.name} coming soon.</p>
      </div>
    );
  }
  const d = f.data;
  return (
    <div>
      <div className="grid lg:grid-cols-[1fr_0.55fr] gap-6 lg:gap-10 items-stretch">
        <div>
          <h3 className="font-display text-2xl lg:text-3xl font-medium text-[#3E2818] tracking-tight leading-tight">{d.title}</h3>
          <p className="text-sm md:text-base text-[#3E2818]/65 mt-3 leading-relaxed">{d.subtitle}</p>

          {/* Overall use case — espresso → caramel gradient */}
          <div className="mt-6 rounded-xl bg-gradient-to-br from-[#2E1B0C] to-[#5A3418] text-[#F7F5F0] p-5 lg:p-6" data-testid="function-usecase">
            <h4 className="font-display text-xl font-medium">{d.useCaseName}</h4>
            <p className="text-[13px] text-white/70 mt-2 mb-5 max-w-2xl leading-relaxed">{d.useCaseDesc}</p>
            <div className="flex flex-wrap items-center gap-2.5">
              {d.journey.map((s, i) => (
                <div key={s} className="flex items-center gap-1.5">
                  <span className="flex items-center gap-2 px-1 py-2">
                    <span className="font-mono2 text-[10px] text-[#FFB84D]">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-[12.5px] font-semibold">{s}</span>
                  </span>
                  {i < d.journey.length - 1 && <ArrowRight size={14} className="text-[#FFB84D]" />}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="lg:h-full">{d.demoUrl ? <Thumb url={d.demoUrl} img={d.demoImg} domain={d.demoDomain} label={d.demoLabel} alt={d.demoAlt} /> : <ComingSoon />}</div>
      </div>

      <p className="font-mono2 text-[11px] uppercase tracking-[0.22em] text-[#8A5730] mt-8 mb-4">{d.agentsHeading}</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {d.agents.map((a) => (
          <div key={a.title} className="rounded-xl bg-white border border-[#3E2818]/10 p-5 hover:border-[#8A5730]/35 hover:shadow-[0_18px_44px_-28px_rgba(62,40,24,0.4)] transition-all duration-300" data-testid={`function-agent-${a.title.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="w-9 h-9 rounded-lg bg-[#8A5730]/10 text-[#8A5730] flex items-center justify-center mb-3"><a.icon size={16} strokeWidth={1.9} /></div>
            <p className="font-semibold text-[#3E2818] text-sm">{a.title}</p>
            <p className="text-[12px] text-[#3E2818]/55 mt-1.5 leading-snug">{a.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export const Functions = () => {
  const [active, setActive] = useState("finance");
  const f = FUNCTIONS.find((x) => x.id === active);
  return (
    <section id="solutions" className="bg-white py-20 lg:py-28" data-testid="functions-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-6xl mb-8">
          <Reveal delay={0.02}>
            <p className="font-mono2 text-[11px] uppercase tracking-[0.28em] text-[#8A5730] mb-4" data-testid="functions-eyebrow">Business Function Solutions</p>
          </Reveal>
          <Reveal delay={0.04}>
            <h2 className="font-display font-medium tracking-tight text-2xl sm:text-3xl lg:text-[2.5rem] text-[#3E2818] leading-[1.12] max-w-4xl" data-testid="functions-title">Transform Business Processes into Agentic Applications</h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-4 text-base text-[#3E2818]/65 leading-relaxed max-w-2xl">Bring intelligence, autonomy and action into the core functions of your business — from Sales and Marketing to Finance, Supply Chain, Customer Service and HR.</p>
          </Reveal>
        </div>

        {/* Underline tab navigation */}
        <Reveal delay={0.1}>
          <div className="flex flex-wrap gap-x-7 gap-y-2 border-b border-[#3E2818]/12 mb-8">
            {FUNCTIONS.map((x) => (
              <button
                key={x.id}
                onClick={() => setActive(x.id)}
                data-testid={`function-tab-${x.id}`}
                className={`relative pb-3 text-[14px] font-semibold whitespace-nowrap transition-colors duration-300 ${active === x.id ? "text-[#3E2818]" : "text-[#3E2818]/45 hover:text-[#3E2818]/70"}`}
              >
                {x.name}
                {active === x.id && <span className="absolute left-0 -bottom-px h-[2px] w-full bg-[#8A5730]" />}
              </button>
            ))}
          </div>
        </Reveal>

        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.4, ease: EASE }}
          >
            <Detail f={f} />
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};
